import { DestroyRef, Injectable, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { captureException } from '@sentry/angular';
import { OperationStatus, ShiftState } from '@wilson/interfaces';
import { ActivitiesGateway } from '@wilson/api/gateway';
import { NzMessageService } from 'ng-zorro-antd/message';
import { distinctUntilChanged, firstValueFrom, merge } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import {
  isSimpleShiftActivities,
  validateExactBreakWithinShift,
  isBreakDurationOptionAvailable,
  getShiftDurationMinutes,
} from './helpers/is-simple-shift-activities';
import {
  SimplifiedShiftDrawerFormService,
  SimplifiedShiftDrawerFormValue,
} from './simplified-shift-drawer-form.service';
import { SimplifiedShiftActivityOperationsService } from './services/simplified-shift-activity-operations.service';
import {
  ReloadedShift,
  SAVEABLE_FIELDS_WITHOUT_PUBLICATION_STATUS_UPDATE,
  SAVEABLE_FIELDS_WITH_PUBLICATION_STATUS_UPDATE,
  SimplifiedShiftAssigneeSaveService,
  SimplifiedShiftDetailsSaveService,
  SimplifiedShiftReloadAndTimelineSyncService,
} from './services/save';
import { SimplifiedDrawerShift } from './types';

export type SimplifiedShiftDrawerSaveMode = 'auto' | 'manual';

const ACTIVITY_SAVEABLE_FIELDS = [
  'shiftDate',
  'startTime',
  'endTime',
  'activityCategoryId',
  'breakOption',
  'breakStartTime',
  'breakEndTime',
  'location',
  'agreementId',
] as const satisfies readonly (keyof SimplifiedShiftDrawerFormValue)[];

/**
 * Facade for simplified-shift-drawer save orchestration.
 *
 * Owns autosave listeners/queues and activity autosave flow.
 * Delegates persistence + timeline sync to focused services under `services/save/`.
 */
@Injectable()
export class SimplifiedShiftDrawerSaveService {
  private readonly destroyRef = inject(DestroyRef);
  private readonly message = inject(NzMessageService);
  private readonly translate = inject(TranslateService);
  private readonly activityOperationsService = inject(
    SimplifiedShiftActivityOperationsService,
  );
  private readonly activitiesGateway = inject(ActivitiesGateway);
  private readonly reloadAndSync = inject(
    SimplifiedShiftReloadAndTimelineSyncService,
  );
  private readonly detailsSave = inject(SimplifiedShiftDetailsSaveService);
  private readonly assigneeSave = inject(SimplifiedShiftAssigneeSaveService);

  /**
   * Intentional scaffolding for a future Save button — not dead code.
   * Default `'auto'` enables debounced autosave. Set to `'manual'` to disable
   * autosave listeners and call `saveCurrentShiftDetails()` from a button instead.
   */
  readonly saveMode = signal<SimplifiedShiftDrawerSaveMode>('auto');

  private formService!: SimplifiedShiftDrawerFormService;
  private getCurrentShift!: () => SimplifiedDrawerShift | null;
  private onSaveStateChange!: (state: {
    isSaving: boolean;
    isSuccessfullySaved: boolean;
    showSavingIndicator?: boolean;
  }) => void;
  private onShiftUpdated!: (shift: ReloadedShift) => void;
  private lastSavedUserId: string | null = null;
  private activitySaveQueue: Promise<void> = Promise.resolve();
  private shiftDetailsSaveQueue: Promise<void> = Promise.resolve();

  initialize(config: {
    formService: SimplifiedShiftDrawerFormService;
    getCurrentShift: () => SimplifiedDrawerShift | null;
    onSaveStateChange: (state: {
      isSaving: boolean;
      isSuccessfullySaved: boolean;
      showSavingIndicator?: boolean;
    }) => void;
    onShiftUpdated: (shift: ReloadedShift) => void;
  }): void {
    this.formService = config.formService;
    this.getCurrentShift = config.getCurrentShift;
    this.onSaveStateChange = config.onSaveStateChange;
    this.onShiftUpdated = config.onShiftUpdated;

    this.setupAutoSaveListeners();
    this.setupAssigneeAutoSaveListener();
    this.setupActivityAutoSaveListeners();
  }

  notifyFormInitialized(shift: SimplifiedDrawerShift): void {
    this.lastSavedUserId = shift.userId ?? null;
  }

  async saveCurrentShiftDetails(
    updatePublicationStatus: boolean,
  ): Promise<void> {
    const currentShift = this.getCurrentShift();
    if (
      !currentShift ||
      !this.detailsSave.areSaveableFieldsValid(this.formService)
    ) {
      return;
    }

    await this.detailsSave.saveShiftDetails({
      currentShift,
      formValue: this.formService.form.getRawValue(),
      updatePublicationStatus,
      formService: this.formService,
      onSaveStateChange: this.onSaveStateChange,
      onShiftUpdated: this.onShiftUpdated,
      onUserIdSynced: (userId) => {
        this.lastSavedUserId = userId;
      },
    });
  }

  async reloadShiftAndUpdateTimeline(
    shiftId: string,
    options?: {
      updatePublicationStatus?: boolean;
      shiftFieldsToUpdate?: { startDate?: string };
    },
  ): Promise<ReloadedShift | null> {
    return this.reloadAndSync.reloadShiftAndUpdateTimeline(shiftId, options);
  }

  private setupAssigneeAutoSaveListener(): void {
    this.formService.form.controls.userId.valueChanges
      .pipe(
        debounceTime(400),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        if (!this.isAutoSaveEnabled()) {
          return;
        }

        void this.triggerAssigneeAutoSave();
      });
  }

  private async triggerAssigneeAutoSave(): Promise<void> {
    const currentShift = this.getCurrentShift();
    const newUserId = this.formService.form.controls.userId.value;

    if (
      !currentShift ||
      !this.formService.form.controls.userId.dirty ||
      newUserId === this.lastSavedUserId
    ) {
      return;
    }

    await this.assigneeSave.saveAssigneeChange({
      currentShift,
      newUserId,
      previousUserId: this.lastSavedUserId,
      formService: this.formService,
      onSaveStateChange: this.onSaveStateChange,
      onShiftUpdated: this.onShiftUpdated,
      onUserIdSynced: (userId) => {
        this.lastSavedUserId = userId;
      },
    });
  }

  private setupAutoSaveListeners(): void {
    merge(
      ...SAVEABLE_FIELDS_WITH_PUBLICATION_STATUS_UPDATE.map(
        (field) => this.formService.form.controls[field].valueChanges,
      ),
    )
      .pipe(debounceTime(400), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (!this.isAutoSaveEnabled()) {
          return;
        }

        void this.triggerAutoSave(true);
      });

    merge(
      ...SAVEABLE_FIELDS_WITHOUT_PUBLICATION_STATUS_UPDATE.map(
        (field) => this.formService.form.controls[field].valueChanges,
      ),
    )
      .pipe(debounceTime(400), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (!this.isAutoSaveEnabled()) {
          return;
        }

        void this.triggerAutoSave(false);
      });
  }

  private setupActivityAutoSaveListeners(): void {
    merge(
      ...ACTIVITY_SAVEABLE_FIELDS.map(
        (field) => this.formService.form.controls[field].valueChanges,
      ),
    )
      .pipe(debounceTime(400), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (!this.isAutoSaveEnabled()) {
          return;
        }

        void this.triggerActivityAutoSave();
      });
  }

  private async triggerActivityAutoSave(): Promise<void> {
    this.activitySaveQueue = this.activitySaveQueue
      .catch(() => {
        /* keep queue alive after a rejected save */
      })
      .then(() => this.runActivityAutoSave());
    await this.activitySaveQueue;
  }

  private async runActivityAutoSave(): Promise<void> {
    this.formService.clearSpuriousDirtyMatchingLastInit();

    const currentShift = this.getCurrentShift();
    if (
      !currentShift ||
      !this.hasDirtyActivityFields() ||
      !this.canEditActivities(currentShift)
    ) {
      return;
    }

    const formValue = this.formService.form.getRawValue();
    const isSimpleMode = this.formService.activitiesViewMode() === 'simple';
    const agreementDirty = this.formService.form.controls.agreementId.dirty;
    const applyAgreement =
      agreementDirty && !this.formService.hasMixedAgreements();

    if (
      isSimpleMode &&
      isSimpleShiftActivities(currentShift.activities) &&
      !this.canSaveSimpleBreakSelection(formValue)
    ) {
      return;
    }

    this.onSaveStateChange({ isSaving: true, isSuccessfullySaved: false });

    try {
      let shiftFieldsToUpdate: { startDate?: string } | undefined;

      if (isSimpleMode && isSimpleShiftActivities(currentShift.activities)) {
        const simpleSaveResult =
          await this.activityOperationsService.saveSimpleShiftActivities({
            shiftId: currentShift.id,
            previousShiftStartDate: currentShift.startDate,
            activities: currentShift.activities,
            formValue,
            applyAgreement,
          });
        if (simpleSaveResult) {
          shiftFieldsToUpdate = simpleSaveResult;
        }
      } else {
        if (
          this.formService.form.controls.shiftDate.dirty &&
          formValue.shiftDate
        ) {
          await this.activityOperationsService.applyDayDeltaToAllActivities(
            currentShift.activities,
            currentShift.startDate,
            formValue.shiftDate,
            currentShift.id,
          );
        }

        if (applyAgreement) {
          const updates = currentShift.activities
            .filter((activity) => !!activity.id)
            .map((activity) => ({
              id: activity.id as string,
              data: { agreementId: formValue.agreementId },
            }));
          if (updates.length) {
            await firstValueFrom(
              this.activitiesGateway.bulkUpdateActivities(updates),
            );
          }
        }
      }

      const reloadedShift = await this.reloadShiftAndUpdateTimeline(
        currentShift.id,
        { updatePublicationStatus: true, shiftFieldsToUpdate },
      );

      if (reloadedShift) {
        this.lastSavedUserId = reloadedShift.userId ?? null;
        this.formService.patchFromShift(reloadedShift, {
          preserveTierTwoPromotion: true,
        });
        this.onShiftUpdated(reloadedShift);
      }

      this.onSaveStateChange({ isSaving: false, isSuccessfullySaved: true });
    } catch (error) {
      captureException(error);
      this.message.error(
        this.translate.instant('general.something_went_wrong'),
      );
      this.onSaveStateChange({ isSaving: false, isSuccessfullySaved: false });
    }
  }

  private canSaveSimpleBreakSelection(
    formValue: SimplifiedShiftDrawerFormValue,
  ): boolean {
    if (formValue.breakOption == null) {
      return true;
    }

    if (!formValue.startTime || !formValue.endTime) {
      return false;
    }

    const shiftDurationMinutes = getShiftDurationMinutes(
      formValue.startTime,
      formValue.endTime,
    );

    if (formValue.breakOption === 30 || formValue.breakOption === 60) {
      return isBreakDurationOptionAvailable(
        shiftDurationMinutes,
        formValue.breakOption,
      );
    }

    return (
      validateExactBreakWithinShift({
        shiftStart: formValue.startTime,
        shiftEnd: formValue.endTime,
        breakStart: formValue.breakStartTime,
        breakEnd: formValue.breakEndTime,
      }) === null
    );
  }

  private canEditActivities(shift: SimplifiedDrawerShift): boolean {
    if (
      shift.state === ShiftState.AcceptedTimes ||
      shift.state === ShiftState.SubmittedToPayrollProvider
    ) {
      return false;
    }

    return shift.activities.every(
      (activity) => activity.operationalStatus === OperationStatus.NotStarted,
    );
  }

  private async triggerAutoSave(
    updatePublicationStatus: boolean,
  ): Promise<void> {
    this.shiftDetailsSaveQueue = this.shiftDetailsSaveQueue
      .catch(() => {
        /* keep queue alive after a rejected save */
      })
      .then(() => this.runShiftDetailsAutoSave(updatePublicationStatus));
    await this.shiftDetailsSaveQueue;
  }

  private async runShiftDetailsAutoSave(
    updatePublicationStatus: boolean,
  ): Promise<void> {
    this.formService.clearSpuriousDirtyMatchingLastInit();

    const currentShift = this.getCurrentShift();
    if (
      !currentShift ||
      this.formService.form.pristine ||
      !this.detailsSave.areSaveableFieldsValid(this.formService)
    ) {
      return;
    }

    await this.detailsSave.saveShiftDetails({
      currentShift,
      formValue: this.formService.form.getRawValue(),
      updatePublicationStatus,
      formService: this.formService,
      onSaveStateChange: this.onSaveStateChange,
      onShiftUpdated: this.onShiftUpdated,
      onUserIdSynced: (userId) => {
        this.lastSavedUserId = userId;
      },
      options: { showSavingIndicator: false },
    });

    // User kept editing shift-detail fields during the in-flight save.
    if (this.shouldRetryShiftDetailsAutoSave()) {
      const latestShift = this.getCurrentShift();
      if (latestShift) {
        await this.detailsSave.saveShiftDetails({
          currentShift: latestShift,
          formValue: this.formService.form.getRawValue(),
          updatePublicationStatus,
          formService: this.formService,
          onSaveStateChange: this.onSaveStateChange,
          onShiftUpdated: this.onShiftUpdated,
          onUserIdSynced: (userId) => {
            this.lastSavedUserId = userId;
          },
          options: { showSavingIndicator: false },
        });
      }
    }
  }

  private shouldRetryShiftDetailsAutoSave(): boolean {
    return (
      this.detailsSave.hasDirtyShiftDetailFields(this.formService) &&
      this.detailsSave.areSaveableFieldsValid(this.formService)
    );
  }

  private hasDirtyActivityFields(): boolean {
    return ACTIVITY_SAVEABLE_FIELDS.some(
      (field) => this.formService.form.controls[field].dirty,
    );
  }

  private isAutoSaveEnabled(): boolean {
    return this.saveMode() === 'auto';
  }
}
